import { useCallback, useEffect, useRef, useState } from 'react';
import { isSafariBrowser } from '../utils/cameraSupport';

async function tryEnableStabilization(track) {
  if (!track?.getCapabilities || !track.applyConstraints) return;
  try {
    const caps = track.getCapabilities();
    const advanced = [];
    if (caps.imageStabilization) {
      const modes = caps.imageStabilization;
      const prefer = Array.isArray(modes)
        ? modes.includes('on')
          ? 'on'
          : modes[0]
        : 'on';
      advanced.push({ imageStabilization: prefer });
    }
    if (caps.exposureMode && Array.isArray(caps.exposureMode) && caps.exposureMode.includes('continuous')) {
      advanced.push({ exposureMode: 'continuous' });
    }
    if (caps.focusMode && Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous')) {
      advanced.push({ focusMode: 'continuous' });
    }
    if (advanced.length) {
      await track.applyConstraints({ advanced });
    }
  } catch (err) {
    // optional — ignore
  }
}

async function applyTorch(track, enabled) {
  if (!track) return false;
  try {
    const capabilities = track.getCapabilities ? track.getCapabilities() : {};
    if (!capabilities.torch) return false;
    await track.applyConstraints({ advanced: [{ torch: Boolean(enabled) }] });
    return true;
  } catch (err) {
    return false;
  }
}

function mapCameraError(err) {
  const name = err?.name || '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return isSafariBrowser()
      ? 'Safari ต้องกด「เปิดกล้อง」ก่อน แล้วกดอนุญาต'
      : 'ไม่อนุญาตใช้กล้อง — กดอนุญาตในเบราว์เซอร์';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'ไม่พบกล้องบนอุปกรณ์นี้';
  }
  if (name === 'NotReadableError') {
    return 'กล้องถูกใช้งานอยู่ที่แอปอื่น';
  }
  if (name === 'SecurityError' || name === 'NotSupportedError') {
    return 'เบราว์เซอร์บล็อกกล้อง — ใช้ HTTPS หรือติดตั้ง cert';
  }
  if (name === 'AbortError') {
    return 'เปิดกล้องถูกยกเลิก — กด「เปิดกล้อง」อีกครั้ง';
  }
  return err?.message || 'ไม่สามารถเปิดกล้องได้';
}

async function openCameraStream() {
  const tries = isSafariBrowser()
    ? [
        { audio: false, video: { facingMode: { ideal: 'environment' } } },
        { audio: false, video: { facingMode: 'environment' } },
        { audio: false, video: true },
      ]
    : [
        {
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        },
        {
          audio: false,
          video: { facingMode: { ideal: 'environment' } },
        },
        { audio: false, video: true },
      ];

  let lastError;
  for (const constraints of tries) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
}

function canvasToBlob(canvas) {
  return new Promise((resolve) => {
    if (canvas.toBlob) {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
            return;
          }
          try {
            const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
            fetch(dataUrl)
              .then((r) => r.blob())
              .then(resolve)
              .catch(() => resolve(null));
          } catch (err) {
            resolve(null);
          }
        },
        'image/jpeg',
        0.92
      );
      return;
    }
    try {
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      fetch(dataUrl)
        .then((r) => r.blob())
        .then(resolve)
        .catch(() => resolve(null));
    } catch (err) {
      resolve(null);
    }
  });
}

/**
 * Safari/iOS: do NOT auto-start camera (needs user gesture).
 * Chrome: auto-start is usually fine after permission.
 */
export function useInspectCamera({ torchEnabled, enabled = true, autoStart = false }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const trackRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const [torchSupported, setTorchSupported] = useState(false);
  const [needsTap, setNeedsTap] = useState(true);

  const attachStream = useCallback(async () => {
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return false;

    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;

    try {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        await playPromise;
      }
      return true;
    } catch (err) {
      setError('กด「เปิดกล้อง」อีกครั้งเพื่อเล่นวิดีโอ');
      return false;
    }
  }, []);

  const stop = useCallback(async () => {
    if (trackRef.current) {
      await applyTorch(trackRef.current, false);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    streamRef.current = null;
    trackRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setReady(false);
    setStarting(false);
  }, []);

  const start = useCallback(async () => {
    if (!enabled) {
      await stop();
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('เบราว์เซอร์ไม่รองรับกล้อง');
      setReady(false);
      return;
    }

    if (!window.isSecureContext) {
      setError('ต้องใช้ HTTPS ถึงจะเปิดกล้องบน Safari ได้');
      setReady(false);
      return;
    }

    setStarting(true);
    setError('');
    setNeedsTap(false);
    await stop();

    try {
      const stream = await openCameraStream();
      streamRef.current = stream;
      const track = stream.getVideoTracks()[0];
      trackRef.current = track;
      await tryEnableStabilization(track);

      let hasTorch = false;
      try {
        const capabilities = track.getCapabilities ? track.getCapabilities() : {};
        hasTorch = Boolean(capabilities.torch);
      } catch (err) {
        hasTorch = false;
      }
      setTorchSupported(hasTorch);

      await new Promise((r) => setTimeout(r, 50));
      const ok = await attachStream();
      setReady(ok);
      if (!ok) setNeedsTap(true);
    } catch (err) {
      setError(mapCameraError(err));
      setReady(false);
      setNeedsTap(true);
    } finally {
      setStarting(false);
    }
  }, [attachStream, enabled, stop]);

  useEffect(() => {
    if (!enabled) {
      stop();
      setNeedsTap(true);
      return undefined;
    }

    if (autoStart && !isSafariBrowser()) {
      start();
    } else {
      setNeedsTap(true);
    }

    return () => {
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (!streamRef.current || !videoRef.current || starting) return;
    attachStream().then((ok) => {
      if (ok) setReady(true);
    });
  }, [attachStream, starting]);

  useEffect(() => {
    if (!ready || !trackRef.current) return;
    applyTorch(trackRef.current, torchEnabled);
  }, [torchEnabled, ready]);

  const captureFrame = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !ready) return null;

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return null;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(video, 0, 0, width, height);

    const blob = await canvasToBlob(canvas);
    return { canvas, blob, width, height };
  }, [ready]);

  return {
    videoRef,
    ready,
    starting,
    error,
    torchSupported,
    needsTap,
    start,
    stop,
    captureFrame,
  };
}
